
const YOUTUBE_REGEX = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)([\w-]{11})(?:[^\s]*)?/g;

const YOUTUBE_DOMAIN_REGEX = /^(?:https?:\/\/)?(?:www\.)?(?:youtube\.com|youtu\.be)\//;

export interface YouTubeMatch {
  id: string;
  url: string;
  startIndex: number;
  endIndex: number;
}

export function extractYouTubeMatches(text: string): YouTubeMatch[] {
  const matches: YouTubeMatch[] = [];
  const regex = new RegExp(YOUTUBE_REGEX.source, 'g');
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) !== null) {
    matches.push({
      id: match[1],
      url: match[0],
      startIndex: match.index,
      endIndex: match.index + match[0].length,
    });
  }

  return matches;
}

export function hasYouTubeUrl(text: string): boolean {
  return new RegExp(YOUTUBE_REGEX.source).test(text);
}

export function isValidYouTubeDomain(url: string): boolean {
  return YOUTUBE_DOMAIN_REGEX.test(url.trim());
}

export function extractVideoId(url: string): string | null {
  if (!isValidYouTubeDomain(url)) return null;
  const regex = new RegExp(YOUTUBE_REGEX.source);
  const match = regex.exec(url);
  return match ? match[1] : null;
}

export function getEmbedUrl(videoId: string): string {
  return `https://www.youtube-nocookie.com/embed/${videoId}`;
}

export function getThumbnailUrl(videoId: string, quality: 'default' | 'hq' | 'maxres' = 'hq'): string {
  const qualityMap = {
    default: 'default',
    hq: 'hqdefault',
    maxres: 'maxresdefault',
  };
  return `https://img.youtube.com/vi/${videoId}/${qualityMap[quality]}.jpg`;
}

export interface ContentSegment {
  type: 'text' | 'youtube';
  content: string; // text content or video ID
  url?: string; // original URL for youtube segments
}

export function parseContentWithYouTube(text: string): ContentSegment[] {
  const matches = extractYouTubeMatches(text);
  if (matches.length === 0) {
    return [{ type: 'text', content: text }];
  }

  const segments: ContentSegment[] = [];
  let lastIndex = 0;

  for (const match of matches) {
    if (match.startIndex > lastIndex) {
      const textBefore = text.slice(lastIndex, match.startIndex).trim();
      if (textBefore) {
        segments.push({ type: 'text', content: textBefore });
      }
    }
    segments.push({ type: 'youtube', content: match.id, url: match.url });
    lastIndex = match.endIndex;
  }

  if (lastIndex < text.length) {
    const remaining = text.slice(lastIndex).trim();
    if (remaining) {
      segments.push({ type: 'text', content: remaining });
    }
  }

  return segments;
}
